import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import type { DashboardTileConfig } from '@/shared/assist/DashboardContract';
import { aiExplorerTileKey } from './AIExplorerTileTypes';
import { runAIExplorerTile, type AIExplorerTileData } from './AIExplorerTileQuery';

/**
 * Loads the live data for a single dashboard tile. Transient (one per rendered
 * tile); re-queries when the config or the page's ambient filters change, and
 * dedupes identical loads so a parent re-render doesn't refetch. The streamed
 * tile config arrives first (and renders its frame/title immediately); this then
 * fills in the data, mirroring {@link ../PinnedTileViewModel}.
 */
@injectable()
export class AIExplorerTileViewModel extends BaseViewModel {
    loading = false;
    error: string | null = null;
    data: AIExplorerTileData | null = null;

    private key: string | null = null;

    constructor(@inject(IAnalyticsGatewayToken) private readonly gateway: IAnalyticsGateway) {
        super();
    }

    async load(config: DashboardTileConfig, pageFilters: AnalyticsFilter[]): Promise<void> {
        const key = aiExplorerTileKey(config, pageFilters);
        if (this.key === key && (this.data !== null || this.loading)) return;
        this.key = key;
        this.loading = true;
        this.error = null;
        this.data = null;
        this.notifyChange();
        try {
            const data = await runAIExplorerTile(this.gateway, config, pageFilters);
            if (this.key !== key) return;
            this.data = data;
        } catch (err) {
            if (this.key !== key) return;
            this.error = err instanceof Error ? err.message : String(err);
        } finally {
            if (this.key === key) {
                this.loading = false;
                this.notifyChange();
            }
        }
    }
}
